import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type StudentMovesPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createStudentMovesPage(config: StudentMovesPageConfig): ComponentType {
  return function StudentMovesPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/student-moves"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Affordable student moves across ${location}, halls, flats, and shared houses`,
  'Flexible vans and crews to suit small loads or full house share moves',
  'Help with carrying, loading, and transporting bulky items like mattresses and desks',
  'Friendly, careful movers who understand tight student budgets and schedules',
];

const baseProcess = (location: string): string[] => [
  `Tell us about your ${location} student move: pick up and drop off, dates, and how much you have to move.`,
  'We provide a clear, student-friendly quote with no hidden fees.',
  'On the day, our team arrives on time, loads your belongings carefully, and secures everything for transport.',
  'We deliver to your new halls, flat, or family home and help you get settled in quickly.',
];

const baseIdealFor = (location: string): string[] => [
  `Students moving into or out of university accommodation in ${location}`,
  'Groups of housemates organising a shared move between rental properties',
  'Parents arranging transport for student belongings at the start or end of term',
];

export const studentMovesPageConfigs: StudentMovesPageConfig[] = [
  {
    path: '/services/student-moves/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Student Moves in Birmingham',
    title: 'Student Moves in Birmingham',
    description: 'VIP House Movers offers affordable, reliable student moves across Birmingham, perfect for halls, student houses, and flat moves.',
    intro: 'With so many universities and student areas across Birmingham, we know what it takes to make student moves quick, affordable, and stress free, whether you are heading into halls, into a shared house, or back home for the summer.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/student-moves/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Student Moves in Wolverhampton',
    title: 'Student Moves in Wolverhampton',
    description: 'Affordable, practical student moves in Wolverhampton from VIP House Movers, tailored for halls, flats, and shared student houses.',
    intro: 'Based in Wolverhampton, our team is perfectly placed to help students moving to and from university accommodation, shared houses, and family homes in and around the city with a friendly and budget-aware service.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
  {
    path: '/services/student-moves/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Student Moves in Staffordshire',
    title: 'Student Moves in Staffordshire',
    description: 'Reliable student moves across Staffordshire by VIP House Movers, helping students in and out of halls, houses, and flats with ease.',
    intro: 'Our Staffordshire student moves service is designed for the realities of student life: tight deadlines, limited budgets, and mixed loads. We handle it all with a calm, organised approach and clear, honest pricing.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/student-moves/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Student Moves in Walsall',
    title: 'Student Moves in Walsall',
    description: 'VIP House Movers provides affordable, flexible student moves in Walsall, ideal for shared houses, flats, and short distance moves.',
    intro: 'Whether you are moving into a shared student house, moving between rentals, or taking belongings home at the end of term, our Walsall team delivers a smooth, affordable student moves service you can rely on.',
    highlights: baseHighlights('Walsall'),
    process: baseProcess('Walsall'),
    idealFor: baseIdealFor('Walsall'),
  },
  {
    path: '/services/student-moves/telford',
    navLabel: 'Telford',
    cardTitle: 'Student Moves in Telford',
    title: 'Student Moves in Telford',
    description: 'Friendly, affordable student moves in Telford from VIP House Movers, covering halls, flats, and shared houses across the area.',
    intro: 'Our Telford student moves service is built around helping you move quickly and affordably, whether you are starting university, switching accommodation, or heading back to the family home with all your essentials.',
    highlights: baseHighlights('Telford'),
    process: baseProcess('Telford'),
    idealFor: baseIdealFor('Telford'),
  },
];

export const studentMovesPages = studentMovesPageConfigs.map((config) => ({
  ...config,
  component: createStudentMovesPage(config),
}));
